import { useEffect, useState } from 'react';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { offlineStorage } from '@/lib/offlineStorage';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Wifi, WifiOff, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

interface OfflineSyncManagerProps {
  onSyncRequest?: () => Promise<void>;
}

export const OfflineSyncManager = ({ onSyncRequest }: OfflineSyncManagerProps) => {
  const isOnline = useOnlineStatus();
  const { user, perfil } = useAuth();
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    if (perfil?.empresa_id) {
      const changes = offlineStorage.getPendingChangesByEmpresa(perfil.empresa_id);
      setPendingCount(changes.length);
    }
  }, [perfil?.empresa_id, isOnline]);

  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
      toast.info('Sin conexión. Los cambios se guardarán localmente.', {
        icon: <WifiOff className="h-4 w-4" />
      });
    } else if (wasOffline && isOnline) {
      // Connection restored - auto sync
      setWasOffline(false);
      toast.success('Conexión restaurada. Sincronizando datos...', {
        icon: <Wifi className="h-4 w-4" />
      });
      if (pendingCount > 0) {
        handleSync();
      }
    }
  }, [isOnline, wasOffline, pendingCount]);

  const handleSync = async () => {
    if (isSyncing || !perfil?.empresa_id) return;

    setIsSyncing(true);
    try {
      if (onSyncRequest) {
        await onSyncRequest();
      }
      
      const remainingChanges = offlineStorage.getPendingChangesByEmpresa(perfil.empresa_id);
      setPendingCount(remainingChanges.length);

      if (remainingChanges.length === 0) {
        toast.success('Datos sincronizados correctamente', {
          icon: <Wifi className="h-4 w-4" />
        });
      }
    } catch (error) {
      console.error('Error syncing:', error);
      toast.error('Error al sincronizar. Se reintentará automáticamente.');
    } finally {
      setIsSyncing(false);
    }
  };

  if (!user || !perfil) return null;

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50">
      {!isOnline && (
        <div className="bg-warning text-warning-foreground px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 animate-in fade-in slide-in-from-top-2">
          <WifiOff className="h-4 w-4" />
          <span className="text-sm font-medium">Sin conexión - Modo offline activado</span>
        </div>
      )}
      
      {isSyncing && isOnline && (
        <div className="bg-primary text-primary-foreground px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 animate-in fade-in slide-in-from-top-2">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span className="text-sm font-medium">Sincronizando datos...</span>
        </div>
      )}
    </div>
  );
};
